@file:OptIn(kotlin.uuid.ExperimentalUuidApi::class)

package com.yogaaicoach.pages.workout

import com.yogaaicoach.common.core.extensions.format
import com.yogaaicoach.common.ui.components.*
import com.yogaaicoach.pages.workout.data.models.Exercise
import com.yogaaicoach.pages.workout.data.models.FocusAreaDataNarrowed
import com.yogaaicoach.pages.workout.data.models.WorkoutData
import com.yogaaicoach.pages.workout.workoutOverview.ui.WorkoutOverviewViewModel
import com.yogaaicoach.scope
import com.yogaaicoach.user.AppViewModel
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import kotlin.js.json
import react.useEffectOnce
import react.useState
import web.cssom.ClassName
import kotlin.time.Duration.Companion.seconds
import kotlin.uuid.Uuid

internal external interface WorkoutOverviewPageProps : Props {
    var viewModel: WorkoutOverviewViewModel
    var appViewModel: AppViewModel
    var workoutGuid: String
    var durationInMinutes: Int
    var isCompleted: Boolean
    var onStartWorkout: (exerciseNumber: Int) -> Unit
    var onCompleteWorkout: () -> Unit
    var onEditWorkout: () -> Unit
    var onNavigateToDashboard: () -> Unit
    var onNavigateToPayment: () -> Unit
    var onNavigateToContactUs: () -> Unit
}

internal val WorkoutOverviewPage = FC<WorkoutOverviewPageProps> { props ->
    var isPageLoaded by useState(false)
    var workoutData by useState<WorkoutData?>(null)
    var nextExercise by useState<Exercise?>(null)
    var focusAreas by useState<List<FocusAreaDataNarrowed>>(emptyList())
    var showRedoDialog by useState(false)
    var selectedFocusArea by useState<FocusAreaDataNarrowed?>(null)

    useEffectOnce {
        if (props.workoutGuid.isEmpty()) {
            props.onNavigateToDashboard()
            return@useEffectOnce
        }
        scope.launch { props.viewModel.isPageLoadedFlow.collect { isPageLoaded = it } }
        scope.launch { props.viewModel.workoutData.collect { workoutData = it } }
        scope.launch { props.viewModel.nextExercise.collect { nextExercise = it } }
        scope.launch { props.viewModel.focusAreas.collect { focusAreas = it } }
        props.viewModel.init(Uuid.parse(props.workoutGuid), isPortraitView = false, isUpdate = false)
    }

    TopBar {
        title = "Workout"
        onSignOut = { props.appViewModel.signOut() }
        onNavigateToDashboard = props.onNavigateToDashboard
        onNavigateToPayment = props.onNavigateToPayment
        onNavigateToContactUs = props.onNavigateToContactUs
    }

    if (!isPageLoaded) {
        LoadingPage {}
        return@FC
    }

    val items = workoutData?.exercises ?: emptyList()
    val hasWorkoutStarted = nextExercise?.exerciseNumber != 1

    selectedFocusArea?.let { fa ->
        FocusAreaDetailDialog(fa) { selectedFocusArea = null }
    }

    if (showRedoDialog) {
        RedoConfirmationDialog(
            onConfirm = { showRedoDialog = false; props.onStartWorkout(1) },
            onDismiss = { showRedoDialog = false }
        )
    }

    div {
        className = ClassName("workout-overview-shell")

        when {
            hasWorkoutStarted && !props.isCompleted -> {
                val workoutGuidParsed = Uuid.parse(props.workoutGuid)
                val onContinue: () -> Unit = if (nextExercise != null) {
                    { props.onStartWorkout(nextExercise!!.exerciseNumber) }
                } else {
                    { props.viewModel.completeWorkout(props.onCompleteWorkout, workoutGuidParsed) }
                }
                button {
                    className = ClassName("primary-button")
                    +"Continue Workout"
                    ArrowForwardIconWhite {}
                    onClick = { onContinue() }
                }
                div {
                    className = ClassName("workout-overview-or-section")
                    p { className = ClassName("workout-overview-or-label"); +"OR" }
                    button {
                        className = ClassName("outlined-button")
                        +"Restart Workout"
                        ArrowForwardIconPrimary {}
                        onClick = { props.onStartWorkout(1) }
                    }
                }
            }
            props.isCompleted -> {
                button {
                    className = ClassName("primary-button")
                    +"Redo Workout"
                    ArrowForwardIconWhite {}
                    onClick = { showRedoDialog = true }
                }
            }
            else -> {
                button {
                    className = ClassName("primary-button")
                    +"Start Workout"
                    ArrowForwardIconWhite {}
                    onClick = { props.onStartWorkout(1) }
                }
            }
        }

        WorkoutSummaryCard(
            durationInMinutes = props.durationInMinutes,
            exerciseCount = items.size,
            onEditClicked = if (!props.isCompleted) { { props.onEditWorkout() } } else null
        )

        if (hasWorkoutStarted) {
            val completedCount = items.count { it.isCompleted }
            WorkoutProgressSection(completed = completedCount, total = items.size)
        }

        if (focusAreas.isNotEmpty()) {
            FocusAreasSection(focusAreas) { selectedFocusArea = it }
        }

        div {
            className = ClassName("workout-section-card")
            p { className = ClassName("workout-section-card-header"); +"Exercises" }
            div {
                className = ClassName("workout-overview-exercises")
                items.forEachIndexed { index, item ->
                    div {
                        className = ClassName("workout-exercise-wrapper")
                        ExerciseListItem(item)
                    }
                    if (index < items.lastIndex) {
                        div { className = ClassName("workout-exercise-divider") }
                    }
                }
            }
        }
    }
}

private fun ChildrenBuilder.WorkoutSummaryCard(
    durationInMinutes: Int,
    exerciseCount: Int,
    onEditClicked: (() -> Unit)?
) {
    div {
        className = ClassName("workout-summary-card")
        div {
            className = ClassName("workout-summary-stats")
            div {
                className = ClassName("workout-summary-stat")
                AccessTimeIcon {}
                div {
                    p { className = ClassName("workout-summary-stat-value"); +"$durationInMinutes min" }
                    p { className = ClassName("workout-summary-stat-label"); +"Duration" }
                }
            }
            div { className = ClassName("workout-summary-divider") }
            div {
                className = ClassName("workout-summary-stat")
                FitnessCenterIcon {}
                div {
                    p { className = ClassName("workout-summary-stat-value"); +exerciseCount.toString() }
                    p { className = ClassName("workout-summary-stat-label"); +"Exercises" }
                }
            }
        }
        if (onEditClicked != null) {
            button {
                className = ClassName("workout-edit-button")
                onClick = { onEditClicked() }
                EditIconGradient {}
                span { +"Edit" }
            }
        }
    }
}

private fun ChildrenBuilder.WorkoutProgressSection(completed: Int, total: Int) {
    val pct = if (total == 0) 0 else (completed * 100) / total
    div {
        className = ClassName("workout-progress-section")
        p { className = ClassName("workout-progress-label"); +"$completed / $total exercises completed" }
        div {
            className = ClassName("workout-progress-track")
            div {
                className = ClassName("workout-progress-fill")
                style = json("width" to "$pct%") as react.CSSProperties
            }
        }
    }
}

private fun ChildrenBuilder.FocusAreasSection(
    focusAreas: List<FocusAreaDataNarrowed>,
    onFocusAreaClicked: (FocusAreaDataNarrowed) -> Unit
) {
    div {
        className = ClassName("workout-section-card")
        p { className = ClassName("workout-section-card-header"); +"Focus Areas" }
        div {
            className = ClassName("workout-focus-chips-container")
            div {
                className = ClassName("workout-focus-chips")
                focusAreas.forEach { fa ->
                    button {
                        className = ClassName("workout-focus-chip")
                        onClick = { onFocusAreaClicked(fa) }
                        span { +fa.name }
                        InfoIconPrimary {}
                    }
                }
            }
        }
    }
}

private fun ChildrenBuilder.ExerciseListItem(item: Exercise) {
    val durationText = (item.durationInSeconds * item.repetitions).seconds.format()
    div {
        className = ClassName("workout-exercise-item${if (item.isCompleted) " workout-exercise-item-completed" else ""}")

        div {
            className = ClassName("workout-exercise-thumbnail")
            val imageUrl = exerciseImageUrl(item.baseData.name)
            if (imageUrl != null) {
                img {
                    src = imageUrl
                    alt = item.baseData.name
                    className = ClassName("workout-exercise-thumbnail-img")
                }
            } else {
                span { className = ClassName("workout-exercise-placeholder-text"); +item.baseData.name.take(2) }
            }
            div {
                className = ClassName("workout-exercise-number-badge")
                +item.exerciseNumber.toString()
            }
        }

        div {
            className = ClassName("workout-exercise-info")
            p { className = ClassName("workout-exercise-name"); +item.baseData.name }
            if (item.baseData.sanskritName != null) {
                p { className = ClassName("workout-exercise-sanskrit"); +item.baseData.sanskritName!! }
            }
            p { className = ClassName("workout-exercise-duration"); +durationText }
        }

        if (item.isCompleted) {
            div {
                className = ClassName("workout-exercise-done-badge")
                CheckIconGradient {}
            }
        }
    }
}

private fun ChildrenBuilder.FocusAreaDetailDialog(
    focusArea: FocusAreaDataNarrowed,
    onDismiss: () -> Unit
) {
    div {
        className = ClassName("dialog-backdrop")
        onClick = { onDismiss() }
        div {
            className = ClassName("dialog-box")
            onClick = { event -> event.stopPropagation() }
            h2 { className = ClassName("dialog-title"); +focusArea.name }
            p { className = ClassName("dialog-body"); +focusArea.focus }
            if (focusArea.benefits.isNotBlank()) {
                p { className = ClassName("workout-focus-dialog-benefits-label"); +"Benefits" }
                p { className = ClassName("dialog-body"); +focusArea.benefits }
            }
            div {
                className = ClassName("dialog-button-row")
                button {
                    className = ClassName("text-button")
                    +"Close"
                    onClick = { onDismiss() }
                }
            }
        }
    }
}

private fun ChildrenBuilder.RedoConfirmationDialog(
    onConfirm: () -> Unit,
    onDismiss: () -> Unit
) {
    div {
        className = ClassName("dialog-backdrop")
        onClick = { onDismiss() }
        div {
            className = ClassName("dialog-box")
            onClick = { event -> event.stopPropagation() }
            h2 { className = ClassName("dialog-title"); +"Redo Workout?" }
            p { className = ClassName("dialog-body"); +"This will restart the workout from exercise 1. Your existing progress will be lost." }
            div {
                className = ClassName("dialog-button-row")
                button {
                    className = ClassName("text-button")
                    +"Cancel"
                    onClick = { onDismiss() }
                }
                button {
                    className = ClassName("text-button")
                    +"Redo"
                    onClick = { onConfirm() }
                }
            }
        }
    }
}
